import { FC, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@oasisprotocol/ui-library/src/components/ui/button'
import { Typography } from '@oasisprotocol/ui-library/src/components/typography'
import { Check, Copy, Wallet } from 'lucide-react'
import { socialMedia } from '../../utils/externalLinks'
import { Ticker } from '../../../types/ticker'

const DEFAULT_CHAIN_ID = 186
const DEFAULT_RPC = 'https://rpc.bout.network'
const DEFAULT_EXPLORER = 'https://explorer.bout.network'
const DEFAULT_CHAIN_NAME = 'OXN'

type EthereumProvider = {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>
}

function getEthereum(): EthereumProvider | undefined {
  return (window as Window & { ethereum?: EthereumProvider }).ethereum
}

function resolveChainConfig() {
  const chainIdDecimal = Number(import.meta.env.REACT_APP_EVM_CHAIN_ID || DEFAULT_CHAIN_ID)
  const rpcUrl = import.meta.env.REACT_APP_EVM_RPC_URL || DEFAULT_RPC
  const explorerUrl = import.meta.env.REACT_APP_PRODUCTION_URLS?.split(',')[0]?.trim() || DEFAULT_EXPLORER
  const chainName = import.meta.env.REACT_APP_EVM_CHAIN_NAME || DEFAULT_CHAIN_NAME
  const nativeCurrency = {
    name: import.meta.env.REACT_APP_EVM_NATIVE_CURRENCY || Ticker.TEST,
    symbol: import.meta.env.REACT_APP_EVM_NATIVE_CURRENCY || Ticker.TEST,
    decimals: 18,
  }
  return {
    chainIdDecimal,
    chainIdHex: `0x${chainIdDecimal.toString(16)}`,
    rpcUrl,
    explorerUrl,
    chainName,
    nativeCurrency,
  }
}

async function copyText(value: string) {
  try {
    await navigator.clipboard.writeText(value)
    return true
  } catch {
    return false
  }
}

export const BuildOnOxn: FC = () => {
  const { t } = useTranslation()
  const config = resolveChainConfig()
  const [walletStatus, setWalletStatus] = useState<'idle' | 'adding' | 'added' | 'error'>('idle')
  const [walletError, setWalletError] = useState<string>()
  const [copied, setCopied] = useState<'rpc' | 'chain' | null>(null)

  const handleCopy = useCallback(async (key: 'rpc' | 'chain', value: string) => {
    const ok = await copyText(value)
    if (ok) {
      setCopied(key)
      window.setTimeout(() => setCopied(null), 1500)
    }
  }, [])

  const handleAddToWallet = useCallback(async () => {
    const ethereum = getEthereum()
    if (!ethereum) {
      setWalletStatus('error')
      setWalletError(t('buildOnOxn.walletMissing'))
      return
    }

    setWalletStatus('adding')
    setWalletError(undefined)
    try {
      await ethereum.request({
        method: 'wallet_addEthereumChain',
        params: [
          {
            chainId: config.chainIdHex,
            chainName: config.chainName,
            nativeCurrency: config.nativeCurrency,
            rpcUrls: [config.rpcUrl],
            blockExplorerUrls: [config.explorerUrl],
          },
        ],
      })
      setWalletStatus('added')
    } catch (err) {
      const message = err instanceof Error ? err.message : t('buildOnOxn.walletFailed')
      setWalletStatus('error')
      setWalletError(message)
    }
  }, [config, t])

  const docsUrl = socialMedia.docs

  return (
    <div className="w-full p-6 md:p-8 relative bg-white border border-[#E3E8EF] rounded-xl flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <Typography className="text-xl md:text-2xl font-bold leading-6 md:leading-8 text-[#0F172A]">
          {t('buildOnOxn.title')}
        </Typography>
        <Typography className="text-[#64748B] leading-5" variant="small">
          {t('buildOnOxn.description')}
        </Typography>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3 rounded-lg border border-[#E3E8EF] bg-[#F8FAFC] px-3 py-2.5">
          <div className="min-w-0">
            <Typography variant="xsmall" className="text-[#64748B]">
              {t('buildOnOxn.chainId')}
            </Typography>
            <Typography className="font-semibold text-[#0F172A] font-mono truncate">
              {config.chainIdDecimal}{' '}
              <span className="text-[#64748B] font-normal">({config.chainIdHex})</span>
            </Typography>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('buildOnOxn.copyChainId')}
            onClick={() => handleCopy('chain', String(config.chainIdDecimal))}
          >
            {copied === 'chain' ? <Check className="size-4 text-[#16A34A]" /> : <Copy className="size-4" />}
          </Button>
        </div>

        <div className="flex items-center justify-between gap-3 rounded-lg border border-[#E3E8EF] bg-[#F8FAFC] px-3 py-2.5">
          <div className="min-w-0">
            <Typography variant="xsmall" className="text-[#64748B]">
              {t('buildOnOxn.rpc')}
            </Typography>
            <Typography className="font-semibold text-[#0F172A] font-mono truncate">{config.rpcUrl}</Typography>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('buildOnOxn.copyRpc')}
            onClick={() => handleCopy('rpc', config.rpcUrl)}
          >
            {copied === 'rpc' ? <Check className="size-4 text-[#16A34A]" /> : <Copy className="size-4" />}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mt-auto">
        <Button type="button" size="lg" className="flex-1 min-w-[140px]" onClick={handleAddToWallet}>
          <Wallet className="size-4" />
          {walletStatus === 'added' ? t('buildOnOxn.added') : t('buildOnOxn.addToWallet')}
        </Button>
        {docsUrl ? (
          <Button type="button" variant="outline" size="lg" className="flex-1 min-w-[140px]" asChild>
            <a href={docsUrl} rel="noopener noreferrer" target="_blank">
              {t('buildOnOxn.docs')}
            </a>
          </Button>
        ) : null}
      </div>

      {walletStatus === 'error' && walletError ? (
        <Typography variant="xsmall" className="text-destructive">
          {walletError}
        </Typography>
      ) : null}
    </div>
  )
}
